import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowRightLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import type { User } from '@/types';

type Company = { id: string; name: string };

type Preview = {
  fromOperadorId: string;
  toOperadorId: string;
  fromOperadorNome: string;
  toOperadorNome: string;
  companyId: string | null;
  titulos: number;
  clientesAfetados: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  users: User[];
  companies: Company[];
  /** Pré-seleciona origem ao abrir a partir da linha do operador. */
  initialFromOperadorId?: string | null;
};

export function TransferCarteiraModal({
  open,
  onClose,
  users,
  companies,
  initialFromOperadorId = null,
}: Props) {
  const operadores = useMemo(
    () => users.filter((u) => u.role === 'OPERADOR' && u.isActive),
    [users]
  );

  const [fromId, setFromId] = useState(initialFromOperadorId || '');
  const [toId, setToId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFromId(initialFromOperadorId || '');
    setToId('');
    setCompanyId('');
    setPreview(null);
  }, [open, initialFromOperadorId]);

  const body = () => ({
    fromOperadorId: fromId,
    toOperadorId: toId,
    ...(companyId ? { companyId } : {}),
  });

  const runPreview = async () => {
    if (!fromId || !toId) {
      toast.error('Selecione origem e destino');
      return;
    }
    if (fromId === toId) {
      toast.error('Origem e destino devem ser diferentes');
      return;
    }
    setLoadingPreview(true);
    setPreview(null);
    try {
      const res = await api.post('/titulos/operators/transfer-preview', body());
      setPreview(res.data?.data as Preview);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Falha na prévia');
    } finally {
      setLoadingPreview(false);
    }
  };

  const runTransfer = async () => {
    if (!preview) {
      toast.error('Gere a prévia antes de confirmar');
      return;
    }
    const ok = window.confirm(
      `Transferir ${preview.clientesAfetados} card(s) / ${preview.titulos} título(s) de "${preview.fromOperadorNome}" para "${preview.toOperadorNome}"?` +
        (preview.companyId ? '\n\nFiltro: uma empresa selecionada.' : '\n\nEscopo: todas as empresas.')
    );
    if (!ok) return;

    setTransferring(true);
    try {
      const res = await api.post('/titulos/operators/transfer', body());
      const data = res.data?.data as Preview & { updatedTitulos?: number };
      toast.success(
        res.data?.message ||
          `${data?.updatedTitulos ?? preview.titulos} título(s) transferido(s)`
      );
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Falha na transferência');
    } finally {
      setTransferring(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Transferir carteira de operador" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Transfere todos os cards (títulos ativos: abertos, vencidos e parciais) de um operador
          para outro. Apenas administradores. Sem filtro de empresa, a operação vale para todas
          as empresas.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Operador origem</label>
            <Select
              value={fromId}
              onValueChange={(v) => {
                setFromId(v);
                setPreview(null);
              }}
              placeholder="Selecione…"
            >
              <option value="">Selecione…</option>
              {operadores.map((o) => (
                <option key={o.id} value={o.id} disabled={o.id === toId}>
                  {o.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Operador destino</label>
            <Select
              value={toId}
              onValueChange={(v) => {
                setToId(v);
                setPreview(null);
              }}
              placeholder="Selecione…"
            >
              <option value="">Selecione…</option>
              {operadores.map((o) => (
                <option key={o.id} value={o.id} disabled={o.id === fromId}>
                  {o.name}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div>
          <label className="text-xs text-muted-foreground mb-1 block">
            Empresa (opcional)
          </label>
          <Select
            value={companyId}
            onValueChange={(v) => {
              setCompanyId(v);
              setPreview(null);
            }}
          >
            <option value="">Todas as empresas</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>

        {preview ? (
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm space-y-1">
            <div className="font-medium flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4" />
              Prévia
            </div>
            <div>
              {preview.fromOperadorNome} → {preview.toOperadorNome}
            </div>
            <div>
              <strong>{preview.titulos}</strong> título(s) ·{' '}
              <strong>{preview.clientesAfetados}</strong> cliente(s)/card(s)
            </div>
            {preview.titulos === 0 ? (
              <p className="text-xs text-amber-700">
                Nenhum título ativo na carteira de origem com os filtros atuais.
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={transferring}>
            Cancelar
          </Button>
          <Button
            variant="outline"
            onClick={() => void runPreview()}
            disabled={loadingPreview || transferring || !fromId || !toId}
          >
            {loadingPreview ? 'Calculando…' : 'Prévia'}
          </Button>
          <Button
            onClick={() => void runTransfer()}
            disabled={transferring || !preview || preview.titulos === 0}
          >
            {transferring ? 'Transferindo…' : 'Confirmar transferência'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
